import React, { useCallback, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';

import { useGetErrorPaymentsSchema } from '~/hooks/apis/vendors/usePaymentSchemas';

import { ErrorPaymentsAddForm } from './ErrorPaymentsAddForm';
import { ErrorPaymentsList } from './ErrorPaymentsList';

interface Props {
  offerId: string;
  vendorId: string;
  errorPayments: Array<Record<string, unknown>>;
  totalErrorPayments: {
    amount: number;
    currency: string;
  };
  onErrorPaymentAdded: (errorPayment: Record<string, unknown>) => void;
  onErrorPaymentUpdated: (errorPayment: Record<string, unknown>) => void;
  onErrorPaymentDeleted: (errorPaymentId: string) => void;
}

export default function ErrorPaymentsPane({
  onErrorPaymentAdded,
  onErrorPaymentUpdated,
  onErrorPaymentDeleted,
  vendorId,
  offerId,
  errorPayments,
  totalErrorPayments,
}: Props) {
  const [showForm, setShowForm] = useState(false);

  const { data: schema, isLoading } = useGetErrorPaymentsSchema();

  const toggleForm = useCallback(() => {
    setShowForm((prevState) => !prevState);
  }, []);

  const handleErrorPaymentAdded = useCallback(
    (errorPayment) => {
      setShowForm(false);
      onErrorPaymentAdded(errorPayment);
    },
    [onErrorPaymentAdded],
  );

  if (isLoading || !vendorId) {
    return <Spinner />;
  }

  return (
    <Box>
      <PageSubheader title="Error Payments">
        {showForm ? (
          <Button startIcon={<RemoveIcon />} variant="text" onClick={toggleForm} aria-label="Hide error payment form">
            Hide error payment form
          </Button>
        ) : (
          <Button startIcon={<AddIcon />} variant="text" onClick={toggleForm} aria-label="Add new error payment">
            Add error payment
          </Button>
        )}
      </PageSubheader>

      {showForm && (
        <Container>
          <ErrorPaymentsAddForm
            schema={schema.post.body}
            offerId={offerId}
            vendorId={vendorId}
            onErrorPaymentAdded={handleErrorPaymentAdded}
          />
        </Container>
      )}

      <Box id="error-payments-pane">
        <ErrorPaymentsList
          schema={schema.post.body}
          errorPayments={errorPayments}
          totalErrorPayments={totalErrorPayments}
          onErrorPaymentUpdated={onErrorPaymentUpdated}
          onErrorPaymentDeleted={onErrorPaymentDeleted}
        />
      </Box>
    </Box>
  );
}
